import { useState } from "react";
import { mockComments } from "@/mocks/community";

interface Post {
  id: string;
  author: string;
  avatar: string;
  niche: string;
  title: string;
  content: string;
  image: string;
  rating: number;
  ratingCount: number;
  comments: number;
  likes: number;
  tags: string[];
  timeAgo: string;
}

interface PostCardProps {
  post: Post;
  onOpen: (post: Post) => void;
}

export default function PostCard({ post, onOpen }: PostCardProps) {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes);
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLiked((prev) => !prev);
    setLikeCount((prev) => (liked ? prev - 1 : prev + 1));
  };

  const handleRate = (e: React.MouseEvent, rating: number) => {
    e.stopPropagation();
    setUserRating(rating);
  };

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-teal-200 transition-all cursor-pointer group"
      onClick={() => onOpen(post)}
    >
      {/* Image */}
      <div className="h-48 overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {post.tags.map((tag) => (
            <span key={tag} className="text-xs bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full font-medium">
              {tag}
            </span>
          ))}
        </div>

        <h3 className="font-semibold text-gray-900 text-sm mb-2 leading-snug">{post.title}</h3>
        <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-2">{post.content}</p>

        {/* Author */}
        <div className="flex items-center gap-2 mb-4">
          <img
            src={post.avatar}
            alt={post.author}
            className="w-7 h-7 rounded-full object-cover object-top"
          />
          <div>
            <p className="text-xs font-semibold text-gray-800">{post.author}</p>
            <p className="text-xs text-gray-400">{post.niche} · {post.timeAgo}</p>
          </div>
        </div>

        {/* Rating Stars */}
        <div className="flex items-center gap-1 mb-4">
          <span className="text-xs text-gray-500 mr-1">Rate:</span>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={(e) => handleRate(e, star)}
              onMouseEnter={(e) => { e.stopPropagation(); setHoverRating(star); }}
              onMouseLeave={(e) => { e.stopPropagation(); setHoverRating(0); }}
              className="cursor-pointer"
            >
              <i
                className={`text-sm ${
                  (hoverRating || userRating) >= star ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"
                }`}
              />
            </button>
          ))}
          <span className="text-xs text-gray-400 ml-1">({post.ratingCount})</span>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between border-t border-gray-50 pt-4">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              liked ? "text-rose-500" : "text-gray-400 hover:text-rose-400"
            }`}
          >
            <i className={`${liked ? "ri-heart-fill" : "ri-heart-line"} text-sm`} />
            {likeCount}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onOpen(post); }}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chat-3-line text-sm" />
            {post.comments} comments
          </button>
          <button
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-share-line text-sm" />
            Share
          </button>
        </div>
      </div>
    </div>
  );
}
